import React, { useState } from 'react';
import { blockUser, unblockUser, muteUser, unmuteUser } from '../../services/api/userService';

/**
 * "…" menu on another player's profile: Block / Unblock and Mute / Unmute.
 * Blocking asks for confirmation first. The other player is never told.
 *
 * Props:
 *  - userId:      the profile's user id (integer, UUID or short code)
 *  - name:        display name for the confirm text
 *  - blockedByMe: current block state
 *  - mutedByMe:   current mute state
 *  - onChange({ blocked_by_me?, muted_by_me? }): called after a successful change
 */
function PlayerSafetyMenu({ userId, name, blockedByMe = false, mutedByMe = false, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (action, patch) => {
    try {
      setBusy(true);
      setError('');
      await action(userId);
      setOpen(false);
      onChange?.(patch);
    } catch (err) {
      setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleBlock = () => {
    if (blockedByMe) {
      run(unblockUser, { blocked_by_me: false });
      return;
    }
    const who = name || 'this player';
    const ok = window.confirm(
      `Block ${who}? They won't be able to message you, send friend requests, follow you or invite you. ` +
      'Any friendship and follows between you are removed. They are not told.'
    );
    if (ok) {
      run(blockUser, { blocked_by_me: true, friend_status: 'none', is_following: false });
    }
  };

  const handleMute = () => {
    run(mutedByMe ? unmuteUser : muteUser, { muted_by_me: !mutedByMe });
  };

  return (
    <div className="pp-safety">
      <button
        type="button"
        className="pp-btn pp-btn--more"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
      >
        {busy ? '...' : '…'}
      </button>
      {open && (
        <div className="pp-safety__menu" role="menu">
          <button type="button" role="menuitem" className="pp-safety__item" onClick={handleMute} disabled={busy}>
            {mutedByMe ? 'Unmute' : 'Mute'}
          </button>
          <button
            type="button"
            role="menuitem"
            className="pp-safety__item pp-safety__item--danger"
            onClick={handleBlock}
            disabled={busy}
          >
            {blockedByMe ? 'Unblock' : 'Block'}
          </button>
        </div>
      )}
      {error && <p className="pp-safety__error" role="alert">{error}</p>}
    </div>
  );
}

export default PlayerSafetyMenu;
